import PROJECT_TASK_FRAGMENT from './ProjectTaskFragment.graphql';
import { useMutation, gql } from '@apollo/client';
import { translate } from 'javascripts/translations/i18n';
import Steven from 'helpers/steven';
import { graphQLIdDecoder } from 'helpers/graphql-utils';
import toast from 'helpers/toast';
import {
  GET_ASSET_PROJECT_TASKS,
  GET_PROJECT_TASKS_COUNT,
} from './useGetAssetProjectTasks';
import { buildUpdatedProjectTasks } from './helpers';

const CREATE_PROJECT_TASK_FROM_PHOTO = gql`
  mutation CreateProjectTaskFromPhoto(
    $input: ProjectTaskCreateFromPhotoInput!
  ) {
    projectTaskCreateFromPhoto(input: $input) {
      errors {
        error
        message
      }
      projectTask {
        ...ProjectTaskFragment
      }
    }
  }
  ${PROJECT_TASK_FRAGMENT}
`;
const useCreateProjectTaskFromPhoto = ({ photoId, projectId }) => {
  const [createProjectTaskMutation] = useMutation(
    CREATE_PROJECT_TASK_FROM_PHOTO,
    {
      awaitRefetchQueries: true,
      onCompleted: (response) => {
        const errors = response.projectTaskCreateFromPhoto?.errors;
        if (errors?.length) {
          toast.error(errors[0].message);
          return;
        }

        const projectTaskId =
          response.projectTaskCreateFromPhoto?.projectTask?.id;
        const projectTaskDbId = graphQLIdDecoder(projectTaskId)?.databaseId;

        if (projectTaskDbId) {
          Steven.entityCrud({
            entity_id: projectTaskDbId,
            entity_type: 'project_task',
            action: 'created',
            source: 'photo',
          });
        }
      },
      onError: () => {
        toast.error(translate('projectTasks.createTaskErrorMsg'));
      },
      update: (cache, { data }) => {
        if (data?.projectTaskCreateFromPhoto?.errors?.length) {
          return;
        }

        const cachedData = cache.readQuery({
          query: GET_ASSET_PROJECT_TASKS,
          variables: { id: photoId },
        });

        const existingTasks = cachedData?.projectTasksForResource ?? [];
        const newTask = data?.projectTaskCreateFromPhoto?.projectTask;

        let insertIndex = existingTasks.findLastIndex(
          (task) => task.position < newTask.position,
        );

        insertIndex += 1;

        const updatedTasks = buildUpdatedProjectTasks({
          existingTasks,
          newTask,
          insertIndex,
        });

        cache.writeQuery({
          query: GET_ASSET_PROJECT_TASKS,
          variables: { id: photoId },
          data: {
            projectTasksForResource: updatedTasks,
          },
        });

        const cachedProjectTaskList = cache.readQuery({
          query: GET_PROJECT_TASKS_COUNT,
          variables: {
            projectId,
          },
        });

        cache.writeQuery({
          query: GET_PROJECT_TASKS_COUNT,
          variables: {
            projectId,
          },
          data: {
            projectTaskList: {
              ...cachedProjectTaskList.projectTaskList,
              tasksCount: cachedProjectTaskList.projectTaskList.tasksCount + 1,
            },
          },
        });
      },
      refetchQueries: ['GetProjectTasks'],
    },
  );

  const createProjectTaskFromPhoto = async ({ details, position }) => {
    const date = new Date();

    await createProjectTaskMutation({
      variables: {
        input: {
          photoId,
          details,
          position,
        },
      },
      optimisticResponse: {
        projectTaskCreateFromPhoto: {
          __typename: 'ProjectTaskCreateFromPhotoPayload',
          errors: [],
          projectTask: {
            __typename: 'ProjectTask',
            id: `optimisticProjectTask-${date.getTime()}`,
            details,
            position,
            completed: false,
            completedAt: null,
            completedBy: null,
            createdBy: null,
            lastUpdatedBy: null,
            updatedAt: null,
            images: [],
            videos: [],
          },
        },
      },
    });
  };

  return {
    createProjectTaskFromPhoto,
  };
};

export default useCreateProjectTaskFromPhoto;
